import React, { useState } from 'react';
import { CARDS, cardRarity, canUpgrade, upgradedId } from '../game/cards';

const RARITY_BORDER = {
  common: '#8a8a8a',
  uncommon: '#00e5ff',
  rare: '#ffd700',
  legendary: '#ff2ea6',
  divine: '#fff2a8',
};

export default function GameCard({ cardId, onClick, disabled, selected, small = false, showCost = true, testid, showUpgradePreview = true }) {
  const c = CARDS[cardId];
  const [previewOpen, setPreviewOpen] = useState(false);
  if (!c) return null;
  const rarity = cardRarity(cardId);
  const foil = c.divine
    ? 'foil-divine'
    : c.cursed
      ? 'foil-cursed'
      : (c.tags?.includes('mog') && c.tags?.includes('rizz') ? 'foil-holo' : '');
  const cls = `slop-card ${c.type} rarity-${rarity} ${disabled ? 'disabled' : ''} ${selected ? 'selected' : ''} ${foil}`;
  const style = small
    ? { transform: 'scale(0.72)', transformOrigin: 'center bottom', borderColor: RARITY_BORDER[rarity] }
    : { borderColor: RARITY_BORDER[rarity] };
  const modifiers = [];
  if (c.divine) modifiers.push({ label: 'DIVINE', color: '#fff2a8' });
  if (c.innate) modifiers.push({ label: 'INNATE', color: '#39ff14' });
  if (c.retain) modifiers.push({ label: 'RETAIN', color: '#7dd3fc' });
  if (c.ethereal) modifiers.push({ label: 'ETHEREAL', color: '#c084fc' });
  if (c.exhaust && !c.ethereal && !c.innate) modifiers.push({ label: 'EXHAUST', color: '#ff2b2b' });
  if (c.cursed) modifiers.push({ label: 'CURSED', color: '#ff007f' });

  // v2.8 QoL #5: card upgrade preview on hover — show +stat diff
  const upgradable = showUpgradePreview && canUpgrade(cardId);
  const upId = upgradable ? upgradedId(cardId) : null;
  const upC = upId ? CARDS[upId] : null;
  const diffs = [];
  if (upC) {
    if (typeof c.cost === 'number' && typeof upC.cost === 'number' && upC.cost !== c.cost) diffs.push({ label: 'COST', from: c.cost, to: upC.cost });
    if (c.damage != null && upC.damage != null && c.damage !== upC.damage) diffs.push({ label: 'DMG', from: c.damage, to: upC.damage });
    if (c.block != null && upC.block != null && c.block !== upC.block) diffs.push({ label: 'BLK', from: c.block, to: upC.block });
    if (c.draw != null && upC.draw != null && c.draw !== upC.draw) diffs.push({ label: 'DRAW', from: c.draw, to: upC.draw });
    if (c.hits != null && upC.hits != null && c.hits !== upC.hits) diffs.push({ label: 'HITS', from: c.hits, to: upC.hits });
    if (c.applyVuln != null && upC.applyVuln != null && c.applyVuln !== upC.applyVuln) diffs.push({ label: 'VULN', from: c.applyVuln, to: upC.applyVuln });
    if (c.applyWeak != null && upC.applyWeak != null && c.applyWeak !== upC.applyWeak) diffs.push({ label: 'WEAK', from: c.applyWeak, to: upC.applyWeak });
    if (c.heal != null && upC.heal != null && c.heal !== upC.heal) diffs.push({ label: 'HEAL', from: c.heal, to: upC.heal });
  }

  return (
    <div
      className={cls}
      onClick={disabled ? undefined : onClick}
      style={style}
      data-testid={testid || `card-${cardId}`}
      onMouseEnter={() => upgradable && setPreviewOpen(true)}
      onMouseLeave={() => setPreviewOpen(false)}
    >
      {showCost && <div className="card-cost" data-testid={`${testid || `card-${cardId}`}-cost`}>{c.cost}</div>}
      <div className="absolute top-1 right-1 text-[8px] font-mono uppercase tracking-wider px-1" style={{ color: RARITY_BORDER[rarity], background: '#000', border: `1px solid ${RARITY_BORDER[rarity]}` }} data-testid={`rarity-${rarity}`}>
        {rarity[0].toUpperCase()}
      </div>
      <div>
        <div className="card-name">{c.name}</div>
        <div className="card-type">{c.type}</div>
      </div>
      <div className="card-art">{c.art}</div>
      <div className="card-desc">{c.desc}</div>
      {modifiers.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {modifiers.map(m => (
            <span key={m.label} className="text-[8px] px-1 uppercase tracking-wider" style={{ background: m.color, color: '#000', fontWeight: 800 }} data-testid={`mod-${m.label.toLowerCase()}`}>{m.label}</span>
          ))}
        </div>
      )}
      {c.tags && c.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {c.tags.map(t => (
            <span key={t} className="text-[8px] px-1 uppercase tracking-wider" style={{ background: tagColor(t), color: '#000', fontWeight: 700 }}>{t}</span>
          ))}
        </div>
      )}
      {previewOpen && diffs.length > 0 && (
        <div
          className="brutal-border-dim bg-[#0a0a0a] px-2 py-1"
          data-testid={`card-upgrade-preview-${cardId}`}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translate(-50%, -6px)',
            minWidth: 130,
            zIndex: 30,
            borderColor: '#ffd700',
            boxShadow: '3px 3px 0 #ffd700',
          }}
        >
          <div className="text-[9px] uppercase font-mono tracking-widest text-[color:#ffd700] font-black">+ UPGRADE PREVIEW</div>
          <div className="flex flex-col gap-0.5 mt-1">
            {diffs.map(d => (
              <div key={d.label} className="text-[10px] font-mono uppercase text-white flex justify-between gap-2">
                <span className="text-[color:var(--text-dim)]">{d.label}</span>
                <span>
                  <span className="text-[color:var(--red)]">{d.from}</span>
                  <span className="mx-1">→</span>
                  <span className="text-[color:var(--neon)]">{d.to}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function tagColor(t) {
  switch(t) {
    case 'rizz': return '#ff69c8';
    case 'mog': return '#ff2b2b';
    case 'ratio': return '#39ff14';
    case 'copium': return '#7dd3fc';
    case 'schizo': return '#c084fc';
    case 'doom': return '#ffb020';
    default: return '#888';
  }
}
